<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';

  interface MobileFieldCardProps {
    label: string;
    value?: string;
    placeholder?: string;
    open?: boolean;
    disabled?: boolean;
    testid?: string;
    paddingContent?: boolean;
    divider?: boolean;
  }

  const props = withDefaults(defineProps<MobileFieldCardProps>(), {
    value: '',
    placeholder: '',
    open: false,
    disabled: false,
    testid: undefined,
    divider: true,
    paddingContent: true,
  });

  interface MobileFieldCardEmits {
    (e: 'toggle', open: boolean): void;
    (e: 'click'): void;
  }

  const emit = defineEmits<MobileFieldCardEmits>();

  const hasValue = computed<boolean>(() => props.value !== '');
  const displayValue = computed<string>(() => props.value || props.placeholder);
</script>

<template>
  <div
    :class="['mobile-field-card', { 'mobile-field-card_open': open, 'mobile-field-card_disabled': disabled }]"
    :data-testid="testid"
  >
    <button
      type="button"
      class="mobile-field-card__summary mobile-field-card__summary_button"
      :disabled="disabled"
      :data-testid="testid ? `${testid}-button` : undefined"
      @click="emit('click')"
    >
      <Typo
        class="mobile-field-card__label"
        :variant="TypoVariant.BodyLRegular"
        tag="span"
      >
        {{ label }}
      </Typo>
      <Typo
        class="mobile-field-card__value"
        :class="{ 'mobile-field-card__value_placeholder': !hasValue }"
        :variant="TypoVariant.BodyLRegular"
        tag="span"
        :data-testid="testid ? `${testid}-value` : undefined"
      >
        {{ displayValue }}
      </Typo>
    </button>
  </div>
</template>

<style scoped lang="scss">
  .mobile-field-card {
    $root: &;

    background: var(--color-bg-primary);
    border-radius: var(--rounding-l);
    box-sizing: border-box;
    width: 100%;

    &_disabled {
      opacity: 0.7;
    }

    &__summary {
      align-items: center;
      box-sizing: border-box;
      display: flex;
      gap: var(--spacing-s);
      height: 48px;
      justify-content: space-between;
      padding: 0 var(--spacing-m);
      width: 100%;

      &_button {
        background: none;
        border: none;
        cursor: pointer;
        font: inherit;
        text-align: left;

        &:disabled {
          cursor: default;
        }
      }
    }

    &__content {
      &_padding {
        padding: 0 var(--spacing-m) var(--spacing-m);
      }
    }

    &__label {
      color: var(--color-text-secondary);
      white-space: nowrap;

      &_opened {
        color: var(--color-text-primary);
      }
    }

    &__value {
      color: var(--color-text-primary);
      max-height: 40px;
      max-width: 171px;
      overflow: hidden;
      text-align: right;
      text-overflow: ellipsis;
      white-space: nowrap;

      &_placeholder {
        color: var(--color-text-secondary);
      }
    }

    &__action {
      align-items: center;
      display: inline-flex;
    }

    &__divider {
      border-top: 1px dashed var(--color-disabled-500);
      margin-bottom: var(--spacing-m);
    }
  }
</style>
